<template>
  <div v-if="!noResultsState" id="serp__filters-list--container"
       class="serp__filters-list--container  q-container-xl q-p-3 q-p-md-2 q-bg-light q-pb-0">
    <UsedFilterContainer />
    <FilterItemContainer v-for="(filter, index) in displayedFilters" :key="filter.field" :filter="filter"
                         :filter-collapsed="isMobileView ? index >= 1 : index >= 3"/>
    <button
      v-if="shouldDisplayToggleButton"
      type="button"
      class="serp__filters-toggle-button"
      @click="toggleFiltersVisibility"
    >
      {{ toggleButtonLabel }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, watch, unref } from 'vue'
import FilterItemContainer from "@/components/SerpPage/FiltersContainer/FiltersList/FilterItemContainer.vue";
import UsedFilterContainer from "@/components/SerpPage/FiltersContainer/UsedFilterContainer.vue";
import {storeToRefs} from "pinia";
import {useFiltersStore} from "@/store/SerpPage/FiltersStore";
import {useSerpResultsStore} from "@/store/SerpPage/SerpResultsStore";
import {useMobileViewStore} from "@/store/MobileViewStore";
import { CustomSmartSearchLabels, SmartSearchTranslationsInterface } from '@/core/types'
import { resolveCustomSmartSearchConfig } from '@/core/customSmartSearchConfig'

const filtersStore = useFiltersStore();
const mobileViewStore = useMobileViewStore();
const {getFilters} = storeToRefs(filtersStore)
const {isMobileView} = storeToRefs(mobileViewStore)
const serpStore = useSerpResultsStore();
const {noResultsState} = storeToRefs(serpStore)

const SmartSearchTranslations = inject('SmartSearchTranslations') as any
const customLabels = (typeof window !== 'undefined'
  ? (window as any)?._QONizer?.customSmartSearchConfig?.labels
  : undefined) as CustomSmartSearchLabels | undefined
const { accordionVisibleItemsLimit } = resolveCustomSmartSearchConfig()
const showAllFilters = ref(false)

const checkboxFiltersCount = computed(() => {
  return getFilters.value.reduce((count, filter) => {
    return filter.type === 'checkbox' ? count + 1 : count
  }, 0)
})

const shouldDisplayToggleButton = computed(() => {
  return accordionVisibleItemsLimit > 0 && checkboxFiltersCount.value > accordionVisibleItemsLimit
})

const displayedFilters = computed(() => {
  if (!shouldDisplayToggleButton.value || showAllFilters.value) {
    return getFilters.value
  }

  let includedCheckboxes = 0

  return getFilters.value.filter((filter) => {
    if (filter.type !== 'checkbox') {
      return true
    }

    if (includedCheckboxes < accordionVisibleItemsLimit) {
      includedCheckboxes += 1
      return true
    }

    return false
  })
})

watch(shouldDisplayToggleButton, (enabled) => {
  if (!enabled) {
    showAllFilters.value = false
  }
})

const normalizeLabel = (label: unknown): string | undefined => {
  if (typeof label !== 'string') {
    return undefined
  }

  const trimmed = label.trim()
  return trimmed || undefined
}

function getShowAllFiltersLabel(): string {
  const customLabel = normalizeLabel(customLabels?.filters?.showAllFilters)
  if (customLabel) {
    return customLabel
  }

  const label = normalizeLabel(unref(SmartSearchTranslations)?.filters?.showAllFilters)
  return label || 'Pokaż wszystkie filtry'
}

function getHideFiltersLabel(): string {
  const customLabel = normalizeLabel(customLabels?.filters?.hideFilters)
  if (customLabel) {
    return customLabel
  }

  const label = normalizeLabel(unref(SmartSearchTranslations)?.filters?.hideFilters)
  return label || 'Ukryj filtry'
}

const toggleButtonLabel = computed(() => showAllFilters.value ? getHideFiltersLabel() : getShowAllFiltersLabel())

function toggleFiltersVisibility() {
  showAllFilters.value = !showAllFilters.value
}

</script>

<style scoped>
#serp__filters-list--container > div:first-child {
  margin-top: 0 !important;
}

.serp__filters-toggle-button {
  background: transparent;
  border: none;
  color: var(--q-red, #d33242);
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
  padding: 0;
  margin: 0.5rem 0 1rem;
}

.serp__filters-toggle-button:focus-visible {
  outline: 2px solid var(--q-red, #d33242);
  outline-offset: 2px;
}
</style>
